'use server'

import { apiRequest, ApiResponse } from '../request'

export type LabIntegrationProtocol = 'HL7_V2' | 'FHIR_R4' | 'ASTM' | 'CUSTOM'

export type LabMessageStatus =
    | 'RECEIVED'
    | 'PARSED'
    | 'MATCHED'
    | 'PROCESSED'
    | 'ERROR'
    | 'IGNORED'

export type LabMessageType =
    | 'ORM'
    | 'ORU'
    | 'ADT'
    | 'ACK'
    | 'QRY'
    | 'DIAGNOSTIC_REPORT'
    | 'BUNDLE'

export interface LabIntegration {
    id: number
    companyId: number
    name: string
    protocol: LabIntegrationProtocol
    host?: string
    port?: number
    apiUrl?: string
    apiKey?: string
    active: boolean
    autoProcess: boolean
    autoMatch: boolean
    webhookUrl?: string
    webhookSecret?: string
    settings?: any
    createdAt: string
    updatedAt: string
    _count?: {
        messages: number
    }
}

export interface LabMessage {
    id: number
    integrationId: number
    integration?: {
        id: number
        name: string
        protocol: LabIntegrationProtocol
    }
    messageType: LabMessageType
    messageId?: string
    rawContent: string
    parsedContent?: any
    status: LabMessageStatus
    errorMessage?: string
    clientId?: number
    client?: {
        id: number
        name: string
        cpf?: string
    }
    examId?: number
    exam?: {
        id: number
        title: string
    }
    examResultId?: number
    processedAt?: string
    createdAt: string
    updatedAt: string
}

export interface CreateLabIntegrationData {
    name: string
    protocol: LabIntegrationProtocol
    host?: string
    port?: number
    apiUrl?: string
    apiKey?: string
    active?: boolean
    autoProcess?: boolean
    autoMatch?: boolean
    settings?: any
}

export interface UpdateLabIntegrationData
    extends Partial<CreateLabIntegrationData> {}

export interface LabIntegrationStats {
    totalIntegrations: number
    activeIntegrations: number
    totalMessages: number
    byStatus: Record<LabMessageStatus, number>
    recentErrors: number
}

// Integrations CRUD
export async function getLabIntegrations(): Promise<
    ApiResponse<LabIntegration[]>
> {
    return apiRequest<LabIntegration[]>('lab-integration/integrations', {})
}

export async function getLabIntegrationsByCompany(
    companyId: number
): Promise<ApiResponse<LabIntegration[]>> {
    return apiRequest<LabIntegration[]>(
        `lab-integration/integrations/company/${companyId}`,
        {}
    )
}

export async function getLabIntegration(
    id: number
): Promise<ApiResponse<LabIntegration>> {
    return apiRequest<LabIntegration>(`lab-integration/integrations/${id}`, {})
}

export async function createLabIntegration(
    data: CreateLabIntegrationData
): Promise<ApiResponse<LabIntegration>> {
    return apiRequest<LabIntegration>(
        'lab-integration/integrations',
        { data },
        'post'
    )
}

export async function updateLabIntegration(
    id: number,
    data: UpdateLabIntegrationData
): Promise<ApiResponse<LabIntegration>> {
    return apiRequest<LabIntegration>(
        `lab-integration/integrations/${id}`,
        { data },
        'put'
    )
}

export async function deleteLabIntegration(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `lab-integration/integrations/${id}`,
        {},
        'delete'
    )
}

export async function testLabIntegration(
    id: number
): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest<{ success: boolean; message: string }>(
        `lab-integration/integrations/${id}/test`,
        {},
        'post'
    )
}

export async function regenerateWebhookSecret(
    id: number
): Promise<ApiResponse<LabIntegration>> {
    return apiRequest<LabIntegration>(
        `lab-integration/integrations/${id}/regenerate-webhook-secret`,
        {},
        'post'
    )
}

// Messages
export async function getLabMessages(filters?: {
    integrationId?: number
    status?: LabMessageStatus
    messageType?: LabMessageType
    startDate?: string
    endDate?: string
}): Promise<ApiResponse<LabMessage[]>> {
    return apiRequest<LabMessage[]>('lab-integration/messages', {
        params: filters,
    })
}

export async function getLabMessage(
    id: number
): Promise<ApiResponse<LabMessage>> {
    return apiRequest<LabMessage>(`lab-integration/messages/${id}`, {})
}

export async function processLabMessage(
    id: number
): Promise<ApiResponse<LabMessage>> {
    return apiRequest<LabMessage>(
        `lab-integration/messages/${id}/process`,
        {},
        'post'
    )
}

export async function matchLabMessage(
    id: number,
    clientId: number,
    examId?: number
): Promise<ApiResponse<LabMessage>> {
    return apiRequest<LabMessage>(
        `lab-integration/messages/${id}/match`,
        { data: { clientId, examId } },
        'post'
    )
}

export async function getLabIntegrationStats(): Promise<
    ApiResponse<LabIntegrationStats>
> {
    return apiRequest<LabIntegrationStats>('lab-integration/stats', {})
}

export const LAB_PROTOCOL_LABELS: Record<LabIntegrationProtocol, string> = {
    HL7_V2: 'HL7 v2.x',
    FHIR_R4: 'FHIR R4',
    ASTM: 'ASTM',
    CUSTOM: 'Custom',
}

export const LAB_MESSAGE_STATUS_LABELS: Record<LabMessageStatus, string> = {
    RECEIVED: 'Recebida',
    PARSED: 'Processada',
    MATCHED: 'Vinculada',
    PROCESSED: 'Finalizada',
    ERROR: 'Erro',
    IGNORED: 'Ignorada',
}

export const LAB_MESSAGE_STATUS_COLORS: Record<LabMessageStatus, string> = {
    RECEIVED: 'badge-info',
    PARSED: 'badge-warning',
    MATCHED: 'badge-secondary',
    PROCESSED: 'badge-success',
    ERROR: 'badge-error',
    IGNORED: 'badge-ghost',
}

export const LAB_MESSAGE_TYPE_LABELS: Record<LabMessageType, string> = {
    ORM: 'Pedido (ORM)',
    ORU: 'Resultado (ORU)',
    ADT: 'ADT',
    ACK: 'Confirmacao (ACK)',
    QRY: 'Consulta (QRY)',
    DIAGNOSTIC_REPORT: 'DiagnosticReport',
    BUNDLE: 'Bundle',
}
